/**
 * Utility functions for handling post-authentication redirects
 */
import { isPlatformAdmin } from "./roles";

/**
 * Determines the appropriate redirect URL based on user role
 * @param {Object} user - The authenticated user object
 * @param {Object} userProfile - The user's profile data
 * @returns {string} - The redirect URL
 */
export const getPostLoginRedirectUrl = (
  user,
  userProfile,
  { isPartnerAdmin = false, isApprovedInstructor = false } = {},
) => {
  // Check if on ProCert domain first (determines which admin dashboard to use)
  const hostname =
    typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const isProCertDev =
    typeof import.meta.env.VITE_SHOW_PROCERT_HOME !== "undefined" &&
    import.meta.env.VITE_SHOW_PROCERT_HOME === "true";
  const isProCertDomain =
    hostname.includes("procertcompliance") ||
    (isProCertDev && hostname === "localhost");

  // Check if user is admin based on multiple criteria (consistent with useAuth and Header)
  const isAdmin = isPlatformAdmin(user, userProfile);

  if (isAdmin) {
    return isProCertDomain ? "/dashboard" : "/admin/dashboard";
  }

  // Partner admin redirect (checked via partners table, not role field)
  // Partner check comes first so dual-role users default to partner dashboard
  if (isPartnerAdmin || userProfile?.role === "partner") {
    return isProCertDomain ? "/dashboard" : "/partner/dashboard";
  }

  // Instructor redirect (checked via instructor_profiles, falls back to role field)
  if (isApprovedInstructor || userProfile?.role === "instructor") {
    return "/instructor/dashboard";
  }

  // Non-admin redirects
  if (isProCertDomain) {
    return "/user/courses";
  }

  // Default redirect for students/regular users on ISOGyaan/SkillCompliance
  return "/user/profile";
};

/**
 * Handles post-registration redirect logic
 * @param {Object} user - The newly registered user object
 * @param {Object} userProfile - The user's profile data (if available)
 * @returns {string} - The redirect URL
 */
export const getPostRegistrationRedirectUrl = (user, userProfile) => {
  // New users are typically students unless explicitly set as admin
  return "/user/profile";
};

/**
 * Handles redirect logic for protected routes when user is not authenticated
 * @param {string} currentPath - The current path user was trying to access
 * @returns {string} - The authentication URL with return path
 */
export const getAuthenticationRedirectUrl = (currentPath = "") => {
  const authUrl = "/login";

  // Store the return path for after authentication
  // Use localStorage so it survives across tabs (e.g. email verification link opened in new tab)
  if (currentPath && currentPath !== "/") {
    localStorage?.setItem("postAuthRedirect", currentPath);
  }

  return authUrl;
};

/**
 * Gets the stored redirect URL after authentication and clears it
 * @returns {string|null} - The stored redirect URL or null
 */
export const getAndClearStoredRedirect = () => {
  // Check localStorage first (persists across tabs — used for signup → email verify flows)
  const lsRedirect = localStorage?.getItem("postAuthRedirect");
  if (lsRedirect) {
    localStorage?.removeItem("postAuthRedirect");
    return lsRedirect;
  }
  // Fallback to sessionStorage for backward compat
  const ssRedirect = sessionStorage?.getItem("postAuthRedirect");
  if (ssRedirect) {
    sessionStorage?.removeItem("postAuthRedirect");
    return ssRedirect;
  }
  return null;
};

/**
 * Validates if a redirect URL is safe (prevents open redirects)
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is safe to redirect to
 */
export const isValidRedirectUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const redirect = url.trim();
  if (!redirect) return false;

  // Only allow relative URLs or URLs from the same origin
  if (
    redirect.startsWith("/") &&
    !redirect.startsWith("//") &&
    !redirect.startsWith("/\\")
  ) {
    return true;
  }

  try {
    const redirectUrl = new URL(redirect);
    const currentOrigin = window.location?.origin;
    return redirectUrl.origin === currentOrigin;
  } catch {
    return false;
  }
};

/**
 * Performs a safe redirect
 * @param {string} url - The URL to redirect to
 * @param {boolean} replace - Whether to replace the current history entry
 */
export const safeRedirect = (url, replace = false) => {
  if (!isValidRedirectUrl(url)) {
    console.warn("Invalid redirect URL:", url);
    return;
  }

  if (replace) {
    window.location?.replace(url);
  } else {
    window.location.href = url;
  }
};
